import { CheckCircle, Clock, PaperPlaneTilt, XLogo } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { approveCreditRequest, fetchCreditRequest, fetchPendingCreditRequests, submitCreditRequest, type CreditRequest, type Quota } from "../lib/api";
import { track } from "../lib/analytics";
import { useAuth } from "../lib/auth";

const X_PROFILE = "https://x.com/kobyof";

export function CreditAccess({ quota }: { quota: Quota | null }) {
  const { user, token } = useAuth();
  const [request, setRequest] = useState<CreditRequest | null>(null);
  const [xHandle, setXHandle] = useState("");
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!user?.emailVerified || quota?.plan === "superuser") return;
    void token().then((value) => value ? fetchCreditRequest(value) : null).then((value) => {
      setRequest(value ?? null);
      if (value) setXHandle(value.xHandle);
    }).catch(() => setMessage("Your credit-request status could not be loaded."));
  }, [quota?.plan, token, user?.emailVerified]);

  if (!user?.emailVerified || quota?.plan === "superuser") return null;
  if (quota?.plan === "paid" || request?.status === "approved") return <section className="credit-access approved"><CheckCircle size={28} weight="fill" /><div><p className="kicker">Extra access active</p><h2>Twenty removals every day.</h2><p>Your follow-and-DM request was approved. Thank you for supporting KleanBG.</p></div></section>;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setWorking(true); setMessage("");
    try {
      const authToken = await token();
      if (!authToken) throw new Error("Sign in again to submit your request.");
      const value = await submitCreditRequest(authToken, xHandle);
      setRequest(value);
      setMessage("Request saved. Koby will review the matching DM manually.");
      track("credit_request_submitted");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The request could not be saved.");
    } finally { setWorking(false); }
  }

  return <section className="credit-access">
    <div className="credit-access-copy"><p className="kicker">Need more room?</p><h2>Follow. DM. Get 20 daily.</h2><p>Follow <strong>@kobyof</strong> on X and send “KleanBG credits.” Then tell us your X handle so the request can be matched and approved manually.</p></div>
    <ol><li><span>1</span><div><strong>Follow @kobyof</strong><small>Open the profile and tap Follow.</small></div><a href={X_PROFILE} target="_blank" rel="noreferrer" onClick={() => track("x_profile_opened", { source: "account_credit_request" })}><XLogo size={18} weight="fill" /> Open X</a></li><li><span>2</span><div><strong>Send a direct message</strong><small>Message “KleanBG credits” from the same handle.</small></div><a href={X_PROFILE} target="_blank" rel="noreferrer" onClick={() => track("x_dm_started", { source: "account_credit_request" })}><PaperPlaneTilt size={18} weight="fill" /> Message</a></li></ol>
    {request?.status === "pending" ? <div className="request-status"><Clock size={22} weight="fill" /><div><strong>Pending manual review</strong><span>Request from @{request.xHandle}. You can update the handle below if needed.</span></div></div> : null}
    <form onSubmit={submit}><label>Your X handle<div className="handle-field"><span>@</span><input aria-label="Your X handle" value={xHandle} onChange={(event) => setXHandle(event.target.value.replace(/^@/, ""))} maxLength={15} pattern="[A-Za-z0-9_]{1,15}" required placeholder="yourhandle" /></div></label><button className="primary-button" disabled={working}>{working ? "Saving request..." : request ? "Update request" : "I followed and sent the DM"}</button></form>
    {message && <p className="credit-message" role="status">{message}</p>}
  </section>;
}

export function AdminCreditRequests({ enabled }: { enabled: boolean }) {
  const { token } = useAuth();
  const [requests, setRequests] = useState<CreditRequest[]>([]);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!enabled) return;
    void token().then((value) => value ? fetchPendingCreditRequests(value) : []).then(setRequests).catch(() => setMessage("The approval queue could not be loaded."));
  }, [enabled, token]);
  if (!enabled) return null;

  async function approve(request: CreditRequest) {
    setMessage("");
    try {
      const authToken = await token();
      if (!authToken) throw new Error("Sign in again to approve requests.");
      await approveCreditRequest(authToken, request.uid);
      setRequests((items) => items.filter((item) => item.uid !== request.uid));
      setMessage(`@${request.xHandle} now has 20 removals per day.`);
      track("credit_request_approved");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Approval failed."); }
  }

  return <section className="admin-requests"><div><p className="kicker">Superuser tools</p><h2>Credit requests</h2><p>Match each handle with the DM on X before approving.</p></div>{requests.length ? <ul>{requests.map((request) => <li key={request.uid}><div><strong>@{request.xHandle}</strong><span>Requested {new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(request.createdAt))}</span></div><button className="secondary-button" onClick={() => void approve(request)}>Approve 20 daily</button></li>)}</ul> : <p className="admin-empty">No requests are waiting.</p>}{message && <p role="status">{message}</p>}</section>;
}
